import React, { ReactElement, useEffect } from 'react'
import {
    MarketDataSocket,
    WalletSocket,
    TradingSocket,
    TradingSocketContext,
    WalletSocketContext,
    MarketDataSocketContext,
    RateConvertContext,
    RateConvert,
} from '@web/walter-lib/stream'
import { useAuth } from '@web/walter-lib/auth'
import { useCommonCMSConfig, useSymbols } from '~api'

interface Props {
    children: ReactElement | null
}

const SOCKET_API_URL = import.meta.env.VITE_SOCKET_API_URL || ''

const MarketData = new MarketDataSocket({ url: `${SOCKET_API_URL}/api/3/ws/public` })
const Wallet = new WalletSocket({ url: `${SOCKET_API_URL}/api/3/ws/wallet` })
const Trading = new TradingSocket({ url: `${SOCKET_API_URL}/api/3/ws/trading` })

const RateConvertEntity = new RateConvert()

/**
 * Socket connection logic
 */
function SocketConnection({ children }: Props) {
    const { data, isValidating } = useCommonCMSConfig()
    const isNewRateService = data?.features?.isNewRateService
    const blockTradingSocket = data?.features?.blockTradingSocket

    const { data: symbols } = useSymbols()
    const { isAuth } = useAuth()

    useEffect(() => {
        if (isNewRateService) {
            if (MarketData.connected) {
                MarketData.disconnect()
            }
            return
        }

        if (symbols && RateConvertEntity && !isValidating) {
            MarketData?.setSymbols(symbols)
            MarketData?.setRateConvert(RateConvertEntity)
            MarketData?.connect()
        }
    }, [symbols, isNewRateService, isValidating])

    useEffect(() => {
        if (isAuth) {
            Wallet?.connect()

            if (isValidating) {
                return
            }

            if (blockTradingSocket) {
                if (Trading.connected) {
                    Trading?.disconnect()
                }
                return
            }

            Trading?.connect()
        } else {
            Wallet?.disconnect()
            Trading?.disconnect()
        }
    }, [isAuth, blockTradingSocket, isValidating])

    return (
        <MarketDataSocketContext.Provider value={MarketData}>
            <WalletSocketContext.Provider value={Wallet}>
                <TradingSocketContext.Provider value={Trading}>
                    <RateConvertContext.Provider value={RateConvertEntity}>{children}</RateConvertContext.Provider>
                </TradingSocketContext.Provider>
            </WalletSocketContext.Provider>
        </MarketDataSocketContext.Provider>
    )
}

export default SocketConnection
